import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowRight, Clock, Users, Gift, Star, TrendingUp, Shield, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import GiveawayCard from '../components/GiveawayCard';
import CountdownTimer from '../components/CountdownTimer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [giveaways, setGiveaways] = useState([]);
  const [featuredGiveaway, setFeaturedGiveaway] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchGiveaways();
  }, []);

  const fetchGiveaways = async () => {
    try {
      const response = await axios.get(`${API}/giveaways?status=active&limit=6`);
      const allGiveaways = response.data.giveaways || [];
      
      // Find featured or use first
      const featured = allGiveaways.find(g => g.featured) || allGiveaways[0];
      setFeaturedGiveaway(featured);
      setGiveaways(allGiveaways.filter(g => g.giveaway_id !== featured?.giveaway_id).slice(0, 4));
    } catch (error) {
      console.error('Error fetching giveaways:', error);
    } finally {
      setLoading(false);
    }
  };

  const stats = [
    { label: 'Homeowners', value: '12,500+', icon: Users },
    { label: 'Giveaways Completed', value: '380+', icon: Gift },
    { label: 'Total Value Given', value: '$2.5M+', icon: TrendingUp }
  ];

  const sponsors = [
    { name: 'RE/MAX', logo: 'https://images.unsplash.com/photo-1496200186974-4293800e2c20?w=100&h=40&fit=crop' },
    { name: 'Century 21', logo: 'https://images.unsplash.com/photo-1496200186974-4293800e2c20?w=100&h=40&fit=crop' },
    { name: 'Keller Williams', logo: 'https://images.unsplash.com/photo-1496200186974-4293800e2c20?w=100&h=40&fit=crop' },
    { name: 'Coldwell Banker', logo: 'https://images.unsplash.com/photo-1496200186974-4293800e2c20?w=100&h=40&fit=crop' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1631048501619-a27088a3bb70?w=1920&q=80&auto=format&fit=crop"
            alt="Bright modern kitchen renovation with marble island"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent" />
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-32">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-2xl"
          >
            {/* Free Entry Badge */}
            <div className="inline-flex items-center gap-2 bg-green-500 text-white font-bold px-4 py-2 rounded-full text-sm uppercase tracking-wider mb-6 urgency-pulse">
              <Gift className="w-4 h-4" />
              100% Free Entry
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight tracking-tight mb-6" style={{ fontFamily: 'Outfit' }}>
              Win a Free Kitchen Makeover
              <span className="block text-[#FF3B30]">Sponsored by Top Realtors</span>
            </h1>

            <p className="text-xl text-white/80 mb-8 leading-relaxed">
              Enter for a chance to win high-value home upgrades worth up to $10,000. 
              No purchase necessary. Sponsored by your local real estate professionals.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4 mb-12">
              <Link to="/giveaways">
                <Button className="h-14 px-8 bg-[#FF3B30] hover:bg-[#DC2626] text-white text-lg font-medium rounded-xl" data-testid="enter-now-btn">
                  Enter Now — It's Free
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/sponsors">
                <Button variant="outline" className="h-14 px-8 bg-white/10 border-white/30 text-white hover:bg-white/20 text-lg font-medium rounded-xl backdrop-blur-sm" data-testid="become-sponsor-btn">
                  Become a Sponsor
                </Button>
              </Link>
            </div>

            {/* Featured Giveaway Countdown */}
            {featuredGiveaway && (
              <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-6 border border-white/20">
                <div className="flex items-center gap-2 text-white/80 mb-3">
                  <Clock className="w-5 h-5" />
                  <span className="font-medium">Current Giveaway Ends In:</span>
                </div>
                <CountdownTimer endDate={featuredGiveaway.end_date} />
              </div>
            )}
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div 
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
        >
          <div className="w-6 h-10 rounded-full border-2 border-white/30 flex items-start justify-center p-2">
            <div className="w-1.5 h-3 bg-white/50 rounded-full" />
          </div>
        </motion.div>
      </section>

      {/* Quote Section (stats temporarily replaced - restore via git history when real numbers exist) */}
      <section className="py-12 bg-neutral-50 border-y border-neutral-200 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <div className="flex flex-col lg:flex-row items-center justify-center gap-10 lg:gap-14 lg:pr-24">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              data-testid="home-quote"
              className="relative shrink-0 w-72 h-72 sm:w-96 sm:h-96 lg:w-[420px] lg:h-[420px]"
            >
              {/* Pointer */}
              <div className="absolute -top-1 left-1/2 -translate-x-1/2 z-20 w-0 h-0 border-l-[14px] border-r-[14px] border-t-[22px] border-l-transparent border-r-transparent border-t-[#171717] drop-shadow-md" />
              {/* Spinning wheel ring */}
              <div
                className="absolute inset-0 rounded-full wheel-spin shadow-2xl border-8 border-white"
                style={{
                  background:
                    'conic-gradient(#FF3B30 0deg 30deg, #171717 30deg 60deg, #FF9500 60deg 90deg, #E5E5E5 90deg 120deg, #FF3B30 120deg 150deg, #171717 150deg 180deg, #FF9500 180deg 210deg, #E5E5E5 210deg 240deg, #FF3B30 240deg 270deg, #171717 270deg 300deg, #FF9500 300deg 330deg, #E5E5E5 330deg 360deg)',
                }}
              />
              {/* Static center with slogan */}
              <div className="absolute inset-[16%] rounded-full bg-white shadow-inner flex flex-col items-center justify-center text-center px-6 z-10">
                <p className="text-2xl sm:text-3xl lg:text-4xl font-bold text-neutral-900 leading-tight" style={{ fontFamily: 'Outfit' }}>
                  You <span className="text-[#FF3B30]">Spin</span>,<br />We <span className="text-[#FF3B30]">Serve</span>!
                </p>
                <p className="text-neutral-500 mt-3 text-xs sm:text-sm max-w-[220px]">
                  Real home upgrades, delivered to real homeowners.
                </p>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative w-full lg:flex-1"
            >
              <img
                src="https://static.prod-images.emergentagent.com/jobs/7626ece3-51a5-4a98-8e4d-5a4ba64801df/images/a6ea59df2c746083527b0c5af980bfc13b4dd9732d18910939b6d0de78b74827.jpeg"
                alt="Excited homeowner celebrating in her new kitchen"
                data-testid="home-quote-image"
                className="w-full h-64 sm:h-80 lg:h-[380px] object-cover rounded-2xl shadow-xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Featured Giveaways */}
      <section className="pt-10 pb-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <div className="flex items-end justify-between mb-12">
            <div>
              <p className="text-[#FF3B30] font-semibold uppercase tracking-wider text-sm mb-2">Active Giveaways</p>
              <h2 className="text-3xl sm:text-4xl font-bold text-neutral-900" style={{ fontFamily: 'Outfit' }}>
                Win These Home Upgrades
              </h2>
            </div>
            <Link to="/giveaways" className="hidden sm:flex items-center gap-2 text-neutral-600 hover:text-[#FF3B30] font-medium transition-colors">
              View All
              <ChevronRight className="w-5 h-5" />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="bg-neutral-100 rounded-2xl h-80 animate-pulse" />
              ))}
            </div>
          ) : giveaways.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="giveaways-grid">
              {giveaways.map((giveaway, index) => (
                <motion.div
                  key={giveaway.giveaway_id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <GiveawayCard giveaway={giveaway} />
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <Gift className="w-16 h-16 text-neutral-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-neutral-600 mb-2">No Active Giveaways</h3>
              <p className="text-neutral-500">Check back soon for new giveaways!</p>
            </div>
          )}

          <Link to="/giveaways" className="sm:hidden flex items-center justify-center gap-2 text-[#FF3B30] font-medium mt-8">
            View All Giveaways
            <ChevronRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 bg-neutral-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <div className="text-center mb-16">
            <p className="text-[#FF3B30] font-semibold uppercase tracking-wider text-sm mb-2">Simple Process</p>
            <h2 className="text-3xl sm:text-4xl font-bold" style={{ fontFamily: 'Outfit' }}>
              How It Works
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { step: '01', title: 'Browse Giveaways', desc: 'Find home upgrade giveaways in your area sponsored by local realtors and brands.' },
              { step: '02', title: 'Enter for Free', desc: 'Complete a simple entry form. No purchase necessary. One entry per household.' },
              { step: '03', title: 'Win & Transform', desc: 'Winners are selected and receive their home upgrade from our certified contractors.' }
            ].map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.15 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="text-6xl font-bold text-[#FF3B30]/20 mb-4" style={{ fontFamily: 'Outfit' }}>{item.step}</div>
                <h3 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Outfit' }}>{item.title}</h3>
                <p className="text-white/70">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-[#FF3B30] font-semibold uppercase tracking-wider text-sm mb-2">Why Spinser</p>
              <h2 className="text-3xl sm:text-4xl font-bold text-neutral-900 mb-6" style={{ fontFamily: 'Outfit' }}>
                The Trusted Platform for Home Giveaways
              </h2>
              <p className="text-lg text-neutral-600 mb-8">
                Spinser connects homeowners with sponsored giveaways from top real estate professionals. 
                Every giveaway is legitimate, professionally managed, and compliant with Canadian contest regulations.
              </p>
              
              <div className="space-y-4">
                {[
                  { icon: Shield, text: 'Verified sponsors & contractors', id: 'verified' },
                  { icon: Star, text: 'No purchase necessary — ever', id: 'no-purchase' },
                  { icon: Users, text: 'Skill-testing question compliant', id: 'compliant' }
                ].map((item) => (
                  <div key={item.id} className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-xl bg-[#FF3B30]/10 flex items-center justify-center">
                      <item.icon className="w-5 h-5 text-[#FF3B30]" />
                    </div>
                    <span className="text-neutral-700 font-medium">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <img
                src="https://static.prod-images.emergentagent.com/jobs/7626ece3-51a5-4a98-8e4d-5a4ba64801df/images/940b9b44a82692b07d3efbaba45fd3e82646e2d9b84ce30287287320affe990e.jpeg"
                alt="Newly renovated bathroom"
                className="rounded-2xl shadow-2xl w-full object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl px-8 py-6" data-testid="could-be-yours-card">
                <p className="text-xl font-bold text-neutral-900" style={{ fontFamily: 'Outfit' }}>This could be yours!</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sponsors CTA */}
      <section className="py-24 bg-[#FF3B30]">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit' }}>
            Are You a Real Estate Professional?
          </h2>
          <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
            Connect with high-intent homeowners in your area. Sponsor giveaways and receive 
            exclusive leads with property-level data.
          </p>
          <Link to="/login">
            <Button className="h-14 px-8 bg-white text-[#FF3B30] hover:bg-neutral-100 text-lg font-medium rounded-xl" data-testid="sponsor-cta-btn">
              Start Sponsoring Today
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-neutral-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
            <div>
              <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Outfit' }}>
                <span className="text-white">Spin</span>
                <span className="text-[#FF3B30]">ser</span>
              </h3>
              <p className="text-white/60">
                The epicentre of homeowner attention — where businesses compete to give value first.
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Platform</h4>
              <ul className="space-y-2 text-white/60">
                <li><Link to="/giveaways" className="hover:text-white transition-colors">Browse Giveaways</Link></li>
                <li><Link to="/login" className="hover:text-white transition-colors">Enter a Giveaway</Link></li>
                <li><Link to="/sponsors" className="hover:text-white transition-colors">Become a Sponsor</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-white/60">
                <li><Link to="/terms" data-testid="footer-terms-link" className="hover:text-white transition-colors">Terms of Service</Link></li>
                <li><Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-white transition-colors">Privacy Policy</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <ul className="space-y-2 text-white/60">
                <li>support@spinser.com</li>
                <li>1-800-SPINSER</li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-white/10 mt-12 pt-8 text-center text-white/40 text-sm">
            <p>© {new Date().getFullYear()} Spinser. No purchase necessary. Skill-testing question required. See official rules for details.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
